<template>
  <form
    class="border-action-default-low rounded-sm relative z-10 border"
    role="search"
    @submit.prevent="submit"
  >
    <Textbox
      :input-id="inputId"
      :input-name="SCHEMA_SEARCH_NAME"
      :isListboxOpen
      :listboxResultsMessage
      :value="inputValue"
      @change="changeHandler"
      @close-listbox="closeListbox"
      @focus="openListbox"
      @keydown="changeSelectedIndex"
      @submit="submit"
    />
    <div
      v-show="isListboxOpen"
      class="bg-overlap-default-low border-static-default-low text-action-default-hi shadow-long rounded-sm absolute inset-x-0 mt-1 max-h-448 divide-y overflow-auto"
    >
      <template v-if="!inputValue">
        <Listbox
          v-if="popularSearches.length > 0"
          :items="popularSearches"
          name="popularSearches"
          :selected-index="selectedIndex"
          :title="i18n(translations.popularSearches)"
          @select="selectIndex"
        />
      </template>

      <template v-if="inputValue">
        <Listbox
          v-if="suggestions"
          :items="suggestions"
          name="suggestions"
          :selected-index="selectedIndex"
          @select="selectIndex"
        />
      </template>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'

import type { AlgoliaAPISearchResultHitHighlighted } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { SearchPopularHit } from '@backmarket/http-api/src/api-specs-search-reco/search/searchPopular'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './Combobox.translations'
import Listbox from './Listbox.vue'
import textboxTranslations from './Textbox.translations'
import Textbox from './Textbox.vue'

export type Item = (AlgoliaAPISearchResultHitHighlighted | SearchPopularHit) & {
  source: string
}

const SCHEMA_SEARCH_NAME = 'q'

const props = withDefaults(
  defineProps<{
    popularSearches?: SearchPopularHit[]
    suggestions?: AlgoliaAPISearchResultHitHighlighted[]
    inputId: string
  }>(),
  {
    popularSearches: () => [],
    suggestions: () => [],
  },
)

const i18n = useI18n()

const selectedIndex = ref(-1)
const isListboxOpen = ref(false)
const inputValue = ref('')

const emit = defineEmits(['change', 'submit'])

const addSource = (
  items: AlgoliaAPISearchResultHitHighlighted[] | SearchPopularHit[],
  source: string,
): Item[] => items.map((item) => ({ ...item, source }))

const items = computed(() => {
  const suggestions = addSource(props.suggestions, 'suggestions')
  const popularSearches = addSource(props.popularSearches, 'popularSearches')

  return inputValue.value ? suggestions : popularSearches
})

const listboxResultsMessage = computed(() => {
  const resultsCount = items.value.length
  const searchQuery = inputValue.value
  const messageWhenQuery = i18n(translations.resultsListing, {
    resultsCount,
    searchQuery,
  })

  return inputValue.value
    ? messageWhenQuery
    : i18n(textboxTranslations.textboxPlaceholder)
})

function changeHandler(value: string) {
  inputValue.value = value
  selectedIndex.value = -1
  emit('change', inputValue.value)
}

function submit() {
  const focusedElement = document.activeElement as HTMLElement
  focusedElement?.blur()

  emit('submit', {
    query: inputValue.value,
    item: items.value?.[selectedIndex.value],
    index: selectedIndex.value,
  })
}

function selectIndex(index: number) {
  selectedIndex.value = index
  submit()
}

function changeSelectedIndex(offset: number) {
  const { length } = items.value
  if (length !== 0) {
    selectedIndex.value = (length + selectedIndex.value + offset) % length
  }
}

function closeListbox() {
  isListboxOpen.value = false
}

function openListbox() {
  selectedIndex.value = -1
  isListboxOpen.value = true
}

onBeforeMount(() => {
  const input = document.querySelector(
    `#${props.inputId}`,
  ) as HTMLInputElement | null

  if (input && input.value) {
    changeHandler(input.value)
  }
})
</script>
